import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Phone, Plus, Check } from "lucide-react";
import { RESTAURANT } from "@/data/catalog";
import { AddToCartButton } from "@/components/AddToCartButton";

/* Règles métier ------------------------------------------------------------ */

const FRIES = [
  { id: "classic", label: "Frites fraîches maison", surcharge: 0 },
  { id: "cheddar", label: "Frites fraîches maison Cheddar", surcharge: 1.0 },
  {
    id: "cheddar-bacon",
    label: "Frites fraîches maison Cheddar + Bacon halal",
    surcharge: 2.0,
  },
];

const DRINKS_STD = [
  "Coca-Cola",
  "Coca-Cola Zéro",
  "Fanta",
  "Orangina",
  "Oasis",
  "Tropico",
  "Sprite",
  "Eau",
];

const DRINK_CATEGORIES = [
  { id: "standard", label: "Standard", surcharge: 0, options: DRINKS_STD },
  { id: "special", label: "Soda spécial", surcharge: 1.0, options: ["Soda spécial"] },
  { id: "lassi", label: "Lassi mangue", surcharge: 2.0, options: ["Lassi mangue"] },
];

const MENU_PRICE = 3.0;

const parsePrice = (str) => {
  if (typeof str !== "string") return 0;
  return parseFloat(str.replace("€", "").replace(",", ".").trim()) || 0;
};

const fmt = (n) => `${n.toFixed(2).replace(".", ",")} €`;

/* -------------------------------------------------------------------------- */

const Card = ({ active, children, onClick, testId, extra }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`w-full flex items-center justify-between gap-3 rounded-lg border px-3 py-3 text-left transition-colors ${
      active
        ? "border-[#FF9500] bg-[#FF9500]/10"
        : "border-[#262626] bg-[#0A0A0A] hover:border-[#FF9500]/40"
    }`}
  >
    <span className="flex items-center gap-2.5">
      <span
        className={`inline-flex h-5 w-5 items-center justify-center rounded-full border ${
          active
            ? "bg-[#FF9500] border-[#FF9500] text-black"
            : "border-[#3a3a3a]"
        }`}
      >
        {active && <Check size={12} strokeWidth={3} />}
      </span>
      <span className="text-sm text-white leading-tight">{children}</span>
    </span>
    {extra && (
      <span className="font-display text-sm text-[#FF9500] whitespace-nowrap">
        {extra}
      </span>
    )}
  </button>
);

/* -------------------------------------------------------------------------- */

export const MenuOptionDialog = ({ product, extras, extraDetail }) => {
  // product = { id, name, price (string formatée) }
  // extras = { label, surcharge } — supplément déjà choisi (ex. "Pain Cheese Naan")
  const [friesId, setFriesId] = useState("classic");
  const [drinkCat, setDrinkCat] = useState("standard");
  const [drinkChoice, setDrinkChoice] = useState(DRINKS_STD[0]);

  const productPrice = parsePrice(product.price);
  const fries = FRIES.find((f) => f.id === friesId);
  const drink = DRINK_CATEGORIES.find((d) => d.id === drinkCat);
  const drinkSurcharge = drink?.surcharge ?? 0;
  const friesSurcharge = fries?.surcharge ?? 0;
  const extraSurcharge = extras?.surcharge ?? 0;

  const total = useMemo(
    () =>
      productPrice + MENU_PRICE + friesSurcharge + drinkSurcharge + extraSurcharge,
    [productPrice, friesSurcharge, drinkSurcharge, extraSurcharge]
  );

  const selectDrinkCategory = (cat) => {
    setDrinkCat(cat.id);
    setDrinkChoice(cat.options[0]);
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid={`menu-option-trigger-${product.id}`}
          className="font-street inline-flex items-center gap-1.5 rounded-md border border-[#FF9500]/40 bg-[#FF9500]/5 px-3 py-1.5 text-[11px] tracking-wider text-[#FF9500] hover:bg-[#FF9500]/15 transition-colors whitespace-nowrap"
        >
          <Plus size={11} strokeWidth={2.6} />
          Ajouter en menu +3 €
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0F0F0F] border border-[#262626] text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-street text-2xl tracking-wide">
            Option Menu — {product.name}
          </DialogTitle>
          <DialogDescription className="text-[11px] italic text-white/55">
            Prix estimatif. Commande à confirmer par téléphone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Frites */}
          <section>
            <div className="font-street text-sm tracking-wider text-[#FF9500] uppercase mb-2">
              Choix des frites
            </div>
            <div className="space-y-2">
              {FRIES.map((f) => (
                <Card
                  key={f.id}
                  active={friesId === f.id}
                  onClick={() => setFriesId(f.id)}
                  testId={`menu-opt-fries-${f.id}`}
                  extra={f.surcharge > 0 ? `+${fmt(f.surcharge)}` : "incluses"}
                >
                  {f.label}
                </Card>
              ))}
            </div>
          </section>

          {/* Boisson */}
          <section>
            <div className="font-street text-sm tracking-wider text-[#FF9500] uppercase mb-2">
              Choix de la boisson
            </div>
            <div className="space-y-2">
              {DRINK_CATEGORIES.map((cat) => (
                <Card
                  key={cat.id}
                  active={drinkCat === cat.id}
                  onClick={() => selectDrinkCategory(cat)}
                  testId={`menu-opt-drink-cat-${cat.id}`}
                  extra={
                    cat.surcharge > 0 ? `+${fmt(cat.surcharge)}` : "incluse"
                  }
                >
                  {cat.label}
                </Card>
              ))}
            </div>
            {drink && drink.options.length > 1 && (
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-street mb-2">
                  Référence
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {drink.options.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setDrinkChoice(opt)}
                      data-testid={`menu-opt-drink-${opt}`}
                      className={`rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
                        drinkChoice === opt
                          ? "bg-[#FF9500] text-black"
                          : "border border-[#262626] bg-[#0A0A0A] text-white/70 hover:text-white"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Récapitulatif */}
          <section className="rounded-xl border border-[#262626] bg-[#0A0A0A] p-4 text-xs space-y-1.5">
            <Row label={product.name} value={fmt(productPrice)} />
            {extras && extras.surcharge > 0 && (
              <Row label={extras.label} value={`+${fmt(extras.surcharge)}`} />
            )}
            <Row label="Option Menu" value={`+${fmt(MENU_PRICE)}`} />
            <Row
              label={fries?.label}
              value={
                friesSurcharge > 0 ? `+${fmt(friesSurcharge)}` : "incluses"
              }
              muted={friesSurcharge === 0}
            />
            <Row
              label={drinkChoice}
              value={
                drinkSurcharge > 0 ? `+${fmt(drinkSurcharge)}` : "incluse"
              }
              muted={drinkSurcharge === 0}
            />
          </section>

          {/* Total */}
          <div
            data-testid="menu-opt-total"
            className="rounded-xl bg-[#FF9500] text-black p-4 flex items-center justify-between"
          >
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold">
                Total estimé
              </div>
              <div className="font-display text-3xl leading-none mt-1">
                {fmt(total)}
              </div>
            </div>
          </div>

          <p className="text-[11px] italic text-white/55 leading-snug">
            Appelez le restaurant avec ce récapitulatif sous les yeux pour
            finaliser votre commande.
          </p>

          <a
            href={RESTAURANT.phoneTel}
            data-testid="menu-opt-call-cta"
            className="w-full font-street inline-flex items-center justify-center gap-2 rounded-md bg-[#FF9500] px-5 py-3.5 text-base tracking-wider text-black shadow-[0_5px_0_#7a4900] hover:-translate-y-0.5 transition-all"
          >
            <Phone size={17} strokeWidth={2.6} />
            Appeler pour commander
          </a>
          <div className="text-center text-[11px] text-white/45">
            {RESTAURANT.phoneDisplay}
          </div>

          <AddToCartButton
            item={{
              kind: "menu",
              name: `${product.name} — Menu`,
              details: [
                `Produit : ${product.name} (${fmt(productPrice)})`,
                ...(extraDetail ? [extraDetail] : []),
                ...(extras && extras.surcharge > 0
                  ? [`${extras.label} (+${fmt(extras.surcharge)})`]
                  : []),
                `Option Menu (+${fmt(MENU_PRICE)})`,
                `Frites : ${fries?.label}${friesSurcharge > 0 ? ` (+${fmt(friesSurcharge)})` : ""}`,
                `Boisson : ${drinkChoice}${drinkSurcharge > 0 ? ` (+${fmt(drinkSurcharge)})` : ""}`,
              ],
              unitPrice: total,
            }}
            testId={`menu-opt-add-to-recap-${product.id}`}
            size="md"
            variant="outline"
            fullWidth
            label="Ajouter au récapitulatif"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, value, muted }) => (
  <div className="flex items-baseline justify-between gap-3">
    <span className={muted ? "text-white/50" : "text-white/85"}>{label}</span>
    <span
      className={
        muted ? "text-white/45 italic" : "font-display text-[#FF9500]"
      }
    >
      {value}
    </span>
  </div>
);

export default MenuOptionDialog;
